/* eslint-disable react/no-unescaped-entities */
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Quote, Users, Network, Compass, ArrowRight } from "lucide-react";
import Seo, { ldOrganization, ldBreadcrumb } from "@/components/Seo";

const PILLARS = [
  {
    icon: Compass,
    title: "Expérience de terrain",
    text: "Près de dix-huit années d'engagement entrepreneurial, au contact direct des problématiques juridiques, fiscales, patrimoniales et stratégiques des dirigeants.",
  },
  {
    icon: Users,
    title: "Connaissance du réseau",
    text: "Une connaissance fine des professions réglementées, de leurs complémentarités et des difficultés rencontrées pour identifier les bons interlocuteurs au bon moment.",
  },
  {
    icon: Network,
    title: "Vision pluridisciplinaire",
    text: "La conviction qu'un dossier complexe se traite rarement seul : il appelle une orchestration entre spécialistes capables de dialoguer intelligemment entre eux.",
  },
];

export default function About() {
  return (
    <div className="bg-[#FAFAF7] min-h-screen">
      <Seo
        title="À propos — méthodologie, déontologie, équipe"
        description="CONFILEX est une plateforme institutionnelle française. Découvrez notre méthodologie d'orientation, notre déontologie (aucun conseil juridique, fiscal ou comptable produit par l'IA) et la sélection rigoureuse de notre réseau de spécialistes."
        canonical="/about"
        keywords={["à propos confilex", "méthodologie", "déontologie IA juridique", "réseau spécialistes France"]}
        jsonLd={[
          ldOrganization(),
          ldBreadcrumb([{ name: "Accueil", url: "/" }, { name: "À propos", url: "/about" }]),
        ]}
      />
      <Navbar variant="light" />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 opacity-[0.12]">
          <img
            src="/art/sorola/promenade-2.jpg"
            alt=""
            className="w-full h-full object-cover"
            style={{ filter: "saturate(0.85)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#FAFAF7]/30 to-[#FAFAF7]/70" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="text-xs uppercase tracking-[0.25em] text-[#C9A961] mb-5">Notre histoire</div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#0B3A2A] leading-[1.05] mb-6 max-w-3xl">
            Une plateforme née d'une <span className="italic text-[#C9A961]">expérience de terrain</span>.
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl leading-relaxed">
            CONFILEX n'est pas un simple projet technologique. C'est le résultat d'années d'engagement professionnel,
            d'opérations menées, de réseaux construits et d'une conviction durable : les dirigeants méritent un environnement
            mieux structuré pour accéder à leurs spécialistes.
          </p>
        </div>
      </section>

      {/* ORIGINE */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-20 lg:py-24" data-testid="about-origine">
        <div className="grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-3">Le fondateur</div>
            <h2 className="font-serif text-3xl text-[#0B3A2A] mb-2">Sofhian Naili</h2>
            <p className="text-sm text-slate-500 mb-6">Fondateur de CONFILEX</p>
            <div className="aspect-[3/4] rounded-2xl overflow-hidden bg-slate-100 border border-slate-200">
              <img
                src="/team/sofhian-naili.png"
                alt="Sofhian Naili, fondateur de CONFILEX"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="md:col-span-8 space-y-6">
            <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961]">Genèse</div>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-[#0B3A2A] leading-tight">
              D'un constat répété, est née une certitude.
            </h2>
            <div className="space-y-5 text-slate-700 leading-relaxed">
              <p>
                Au fil de près de dix-huit années d'expérience entrepreneuriale, Sofhian Naili a été
                confronté à des problématiques que connaissent bien les dirigeants : le choix du bon spécialiste,
                la difficulté à identifier l'interlocuteur réellement adapté à une situation, le manque de
                coordination entre les professions, l'absence d'environnements véritablement pluridisciplinaires.
              </p>
              <p>
                Des opérations menées, des projets aboutis, des succès remportés, mais aussi des difficultés
                rencontrées — toujours la même conclusion s'imposait&nbsp;: il manquait un espace structuré
                permettant d'accéder, simplement et rapidement, à des spécialistes qualifiés capables d'interagir
                intelligemment entre eux.
              </p>
              <p>
                CONFILEX a été pensé pour répondre à ce besoin : non pas comme une marketplace générique, mais
                comme un <strong className="text-[#0B3A2A]">environnement collaboratif</strong>, un
                <strong className="text-[#0B3A2A]"> réseau de spécialistes</strong> et un
                <strong className="text-[#0B3A2A]"> espace d'interconnexion professionnelle</strong>, favorisant
                la qualité des échanges, la complémentarité des compétences et une approche plus fluide des
                problématiques complexes.
              </p>
            </div>

            <div className="border-l-2 border-[#C9A961] pl-6 py-2 my-10">
              <Quote className="w-6 h-6 text-[#C9A961] mb-3" />
              <p className="font-serif text-xl md:text-2xl text-[#0B3A2A] italic leading-snug">
                Permettre à d'autres dirigeants de gagner du temps, d'éviter certaines erreurs et d'accéder plus
                rapidement aux bons interlocuteurs — voilà la raison d'être de CONFILEX.
              </p>
              <div className="text-sm text-slate-500 mt-4">— Sofhian Naili, fondateur</div>
            </div>
          </div>
        </div>
      </section>

      {/* PILIERS */}
      <section className="bg-white border-y border-slate-200" data-testid="about-pillars">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="max-w-2xl mb-14">
            <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-3">Fondements</div>
            <h2 className="font-serif text-3xl md:text-4xl text-[#0B3A2A] tracking-tight">
              Trois convictions, une démarche.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {PILLARS.map(({ icon: Icon, title, text }) => (
              <div key={title} className="space-y-4">
                <div className="w-11 h-11 rounded-xl bg-[#C9A961]/10 border border-[#C9A961]/30 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#C9A961]" />
                </div>
                <h3 className="font-serif text-xl text-[#0B3A2A]">{title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VISION */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-20 lg:py-24 text-center" data-testid="about-vision">
        <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-3">Notre vision</div>
        <h2 className="font-serif text-3xl md:text-4xl text-[#0B3A2A] tracking-tight mb-6 leading-tight">
          Moderniser, sans dénaturer, les interactions entre dirigeants et spécialistes.
        </h2>
        <p className="text-slate-600 leading-relaxed mb-10">
          Construire un cadre où la technologie sert la qualité relationnelle plutôt qu'elle ne la remplace.
          Permettre à chaque professionnel référencé d'exercer dans un environnement à la hauteur de son expertise,
          et à chaque dirigeant d'accéder à une sélection rigoureuse, en toute confidentialité.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button asChild className="rounded-full bg-[#C9A961] hover:bg-[#E3C77A] text-[#0B3A2A] font-semibold h-11 px-8">
            <Link to="/missions/new" data-testid="about-cta-mission">Soumettre une demande <ArrowRight className="w-4 h-4 ml-2" /></Link>
          </Button>
          <Button asChild variant="outline" className="rounded-full h-11 px-8 border-slate-300">
            <Link to="/expert/apply" data-testid="about-cta-apply">Rejoindre le réseau</Link>
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
